import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { requestApi } from '../services/api';
import { isAuthenticated, authApi } from '../services/auth';
import RequestTable from '../components/RequestTable';
import Pagination from '../components/Pagination';

const ACTIVE_REQUEST_STATUSES = new Set(['PENDING', 'PROCESSING']);
const ACTIVE_REFRESH_INTERVAL_MS = 5000;

const RequestList = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backgroundRefreshing, setBackgroundRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    category: '',
    status: '',
    page: 1,
    pageSize: 100,
    searchString: '',
    fromDate: '',
    toDate: '',
    orderBy: 'id',
    orderDirection: 'desc',
  });
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    page: 1,
  });
  const backgroundRefreshInFlightRef = useRef(false);
  
  const navigate = useNavigate();

  // Check if user is authenticated
  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
    }
  }, [navigate]);

  // Fetch requests
  const fetchRequests = useCallback(async ({ silent = false } = {}) => {
    if (silent && backgroundRefreshInFlightRef.current) {
      return;
    }

    try {
      if (silent) {
        backgroundRefreshInFlightRef.current = true;
        setBackgroundRefreshing(true);
      } else {
        setLoading(true);
      }
      
      const response = await requestApi.getRequests(filters);
      
      if (response.data && response.data.response) {
        setRequests(response.data.response.data);
        setPagination(response.data.response.pagination || {
          total: 0,
          totalPages: 0,
          page: 1,
        });
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching requests:', err);
      setError('Failed to load requests. Please try again.');
      
      // If unauthorized, redirect to login
      if (err.response && err.response.status === 401) {
        authApi.logout();
        navigate('/login');
      }
    } finally {
      if (silent) {
        backgroundRefreshInFlightRef.current = false;
        setBackgroundRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  }, [filters, navigate]);

  // Initial fetch
  useEffect(() => {
    fetchRequests();
  }, [filters.page, filters.pageSize, filters.status, filters.category, filters.searchString, filters.fromDate, filters.toDate, filters.orderBy, filters.orderDirection, fetchRequests]); // Re-fetch when filters change

  const hasActiveRequests = requests.some((request) =>
    ACTIVE_REQUEST_STATUSES.has(request.status)
  );

  useEffect(() => {
    if (!hasActiveRequests) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      fetchRequests({ silent: true });
    }, ACTIVE_REFRESH_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [hasActiveRequests, fetchRequests]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setFilters(prev => ({
      ...prev,
      searchString: searchQuery,
      page: 1, // Reset to first page when searching
    }));
  };

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
      page: 1, // Reset to first page when filtering
    }));
  };

  // Handle status filter from column header
  const handleStatusFilter = (status) => {
    setFilters((prev) => ({
      ...prev,
      status: status,
      page: 1, // Reset to first page when filtering
    }));
  };
  
  // Handle sorting
  const handleSort = (field, direction) => {
    setFilters((prev) => ({
      ...prev,
      orderBy: field,
      orderDirection: direction,
    }));
  };

  // Apply filters
  const applyFilters = () => {
    fetchRequests();
    setShowFilters(false);
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      category: '',
      status: '',
      page: 1,
      pageSize: 100,
      searchString: searchQuery,
      fromDate: '',
      toDate: '',
      orderBy: 'id',
      orderDirection: 'desc',
    });
    setShowFilters(false);
  };

  // Handle status update
  const handleStatusUpdate = (id, newStatus) => {
    setRequests((prevRequests) =>
      prevRequests.map((request) =>
        request.id === id ? { ...request, status: newStatus } : request
      )
    );
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setFilters((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  // Handle page size change
  const handlePageSizeChange = (e) => {
    const newPageSize = parseInt(e.target.value);
    setFilters((prev) => ({
      ...prev,
      pageSize: newPageSize,
      page: 1, // Reset to first page when changing page size
    }));
  };

  // Handle export
  const handleExport = async () => {
    try {
      setLoading(true);
      const response = await requestApi.exportRequests(filters);
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with current date
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0];
      link.download = `requests_export_${dateStr}.csv`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting requests:', err);
      setError('Failed to export requests. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    authApi.logout();
    navigate('/login');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-title-md font-semibold text-gray-800 dark:text-white/90">
              Requests
            </h1>
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${error ? 'bg-error-500' : hasActiveRequests ? 'bg-warning-500' : 'bg-success-500'}`}></div>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {error ? 'Connection Error' : hasActiveRequests ? (backgroundRefreshing ? 'Checking active tasks...' : 'Watching active tasks') : 'Connected'}
              </span>
            </div>
          </div>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            A list of all requests in the Autobot platform. Click on any row to view detailed request data.
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center xl:justify-end">
          <form onSubmit={handleSearch} className="flex items-center">
            <div className="relative">
              <input
                type="text"
                placeholder="Search requests..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-10 pr-4 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-none focus:ring-4 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-800 dark:text-white sm:w-64"
              />
              <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </form>
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className="autobot-button-secondary"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Filters
          </button>
          <button 
            onClick={fetchRequests}
            disabled={loading}
            className="autobot-button-secondary"
          >
            <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          <button 
            onClick={handleExport}
            className="autobot-button-secondary"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export
          </button>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 shadow-theme-sm p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Category
              </label>
              <input
                type="text"
                id="category"
                name="category"
                value={filters.category}
                onChange={handleFilterChange}
                placeholder="Filter by category"
                className="w-full rounded-lg border border-gray-200 bg-transparent py-2 px-3 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-none focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
              />
            </div>

            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                className="w-full rounded-lg border border-gray-200 bg-transparent py-2 px-3 text-sm text-gray-800 focus:border-brand-300 focus:outline-none focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
              >
                <option value="">All Statuses</option>
                <option value="PENDING">PENDING</option>
                <option value="PROCESSING">PROCESSING</option>
                <option value="FINISHED">FINISHED</option>
                <option value="ERROR">ERROR</option>
              </select>
            </div>

            <div>
              <label htmlFor="fromDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                From Date
              </label>
              <input
                type="date"
                id="fromDate"
                name="fromDate"
                value={filters.fromDate}
                onChange={handleFilterChange}
                className="w-full rounded-lg border border-gray-200 bg-transparent py-2 px-3 text-sm text-gray-800 focus:border-brand-300 focus:outline-none focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
              />
            </div>

            <div>
              <label htmlFor="toDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                To Date
              </label>
              <input
                type="date"
                id="toDate"
                name="toDate"
                value={filters.toDate}
                onChange={handleFilterChange}
                className="w-full rounded-lg border border-gray-200 bg-transparent py-2 px-3 text-sm text-gray-800 focus:border-brand-300 focus:outline-none focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-800">
            <button 
              onClick={resetFilters}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-700"
            >
              Reset
            </button>
            <button 
              onClick={applyFilters}
              className="autobot-button-primary"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-brand-500 border-t-transparent"></div>
        </div>
      ) : error ? (
        <div className="bg-error-50 border border-error-200 text-error-700 px-4 py-3 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <strong className="font-bold">Error loading requests!</strong>
              <span className="block sm:inline mt-1">{error}</span>
            </div>
            <button
              onClick={fetchRequests}
              className="px-3 py-1 bg-error-600 text-white rounded-md hover:bg-error-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      ) : (
        <>
          <RequestTable
            requests={requests}
            onStatusUpdate={handleStatusUpdate}
            onRefresh={fetchRequests}
            onFilterStatus={handleStatusFilter}
            currentStatusFilter={filters.status}
            onSort={handleSort}
            initialSortField={filters.orderBy}
            initialSortDirection={filters.orderDirection}
          />

          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            totalItems={pagination.total}
            itemsPerPage={filters.pageSize}
            onPageChange={handlePageChange}
            onItemsPerPageChange={(newPageSize) => {
              setFilters((prev) => ({
                ...prev,
                pageSize: newPageSize,
                page: 1, // Reset to first page when changing page size
              }));
            }}
            itemsPerPageOptions={[10, 20, 50, 100, 200]}
            showItemsPerPage={true}
            showResultsInfo={true}
          />
        </>
      )}
    </div>
  );
};

export default RequestList;
